import { useRef } from 'react';
import { StatusChip } from '../../design-system/components/StatusChip';
import { SkeletonLoader } from '../../design-system/components/SkeletonLoader';
import { useTaskLog } from '../../api/hooks/useTaskLog';
import { formatDateTime, formatDuration } from '../../utils/date-format';
import { useDismissOnOutside } from '../../shell/use-dismiss-on-outside';
import { TaskTimeline } from './TaskTimeline';
import { TaskDistanceRows } from './TaskDistanceRows';
import { SUBMITTED_TOOLTIP, statusKind } from './task-columns';
import type { OlderTask } from './older-tasks';
import { taskTypeText } from './clean-modes';
import { unassignedText } from './calendar/lane-layout';
import './TaskDetailDrawer.css';
import './TaskPhasesList.css';

/**
 * The panel for a task from the older view: what the stored record says,
 * and its Timeline. READ-ONLY BY CONSTRUCTION.
 *
 * Not `TaskDetailDrawer` with the buttons hidden. That drawer is built
 * around a LIVE task -- Cancel and Kill, the dust-bag confirm, "Show
 * where", the attention banner, the planned-places read -- and every one
 * of those either acts on a robot or reads live state. A finished task
 * from months ago has none of that to offer, and a drawer that merely
 * withholds them is one refactor away from offering Kill on a record.
 * This one never imports a control.
 *
 * The Timeline is the same one, read the same way (`useTaskLog`): the
 * log lines are kept exactly as long as this record is.
 */
/** `5.9 m²` under 10 m², whole square metres above (`1,204 m²`). */
function formatAreaM2(m2: number): string {
  const digits = m2 < 10 ? 1 : 0;
  return `${m2.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} m²`;
}

export function OlderTaskDrawer({ task, onClose }: { task: OlderTask; onClose: () => void }) {
  const panelRef = useRef<HTMLElement>(null);
  useDismissOnOutside(panelRef, onClose);
  const log = useTaskLog(task.id);

  return (
    <aside
      ref={panelRef}
      className="nx-task-drawer"
      role="dialog"
      aria-label={`Task ${task.id} details`}
      data-testid="older-task-drawer"
    >
      <header className="nx-task-drawer__head">
        <h2>{task.id}</h2>
        <button type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="nx-task-drawer__body">
        <p className="nx-task-drawer__sub" data-testid="older-task-readonly">
          This job has finished. This is its record, and nothing here can be changed.
        </p>
        {task.synthetic && (
          <p className="nx-task-synthetic" role="note">
            Closed by the platform — no robot reported this task as failed.
          </p>
        )}
        {task.correctedFrom && (
          <p className="nx-task-drawer__sub" data-testid="older-task-corrected">
            First recorded as {task.correctedFrom}, then corrected.
          </p>
        )}
        <div className="nx-task-drawer__row">
          <span>Status</span>
          <span>
            <StatusChip status={statusKind(task.status)} label={task.status} />
          </span>
        </div>
        <div className="nx-task-drawer__row">
          <span>Type</span>
          <span>{taskTypeText(task.type, task.cleanMode)}</span>
        </div>
        <div className="nx-task-drawer__row">
          <span>Robot</span>
          {/* Every task here has ended: one with no robot never got one
              (the calendar's words, 2026-10-31). */}
          <span>{task.assignedRobot ?? unassignedText(true)}</span>
        </div>
        <div className="nx-task-drawer__row">
          <span>Triggered by</span>
          <span>{task.triggeredBy ?? '—'}</span>
        </div>
        <div className="nx-task-drawer__row">
          <span title={SUBMITTED_TOOLTIP}>Submitted</span>
          <span>{formatDateTime(task.submittedAtMs)}</span>
        </div>
        <div className="nx-task-drawer__row">
          <span>Started</span>
          <span>{formatDateTime(task.startedAtMs)}</span>
        </div>
        <div className="nx-task-drawer__row">
          <span>Finished</span>
          <span data-testid="older-task-finished">{formatDateTime(task.completedAtMs)}</span>
        </div>
        <div className="nx-task-drawer__row">
          <span>Task time</span>
          <span>{formatDuration(task.durationSec)}</span>
        </div>
        {/* The record's own copy of the totals, as the older table's
            columns read it -- never "so far": the job has ended. */}
        <TaskDistanceRows view={task.distance} />
        {/* The robot's own report of the area it covered. Only when it sent
            one: no report is not 0 m². */}
        {task.cleanedAreaM2 !== undefined && (
          <div className="nx-task-drawer__row">
            <span>Area cleaned</span>
            <span data-testid="older-task-area-cleaned">
              {formatAreaM2(task.cleanedAreaM2)}
            </span>
          </div>
        )}

        {/* "Places", not "Visited": the record keeps every place the
            job's steps named, and a cancelled job may not have reached
            all of them. A clean's steps name no place, so its zone is
            the place. */}
        <section className="nx-task-drawer__section">
          <h3>{task.cleanZone ? 'Zone' : 'Places'}</h3>
          {task.cleanZone ? (
            <p>{task.cleanZone}</p>
          ) : task.visitedPlaces.length > 0 ? (
            <ol className="nx-task-phases" data-testid="older-task-places">
              {task.visitedPlaces.map((place, i) => (
                <li key={`${i}-${place}`} className="nx-task-phases__item">
                  <span className="nx-task-phases__label">{place}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="nx-task-phases__empty">No places were recorded for this job.</p>
          )}
        </section>

        <section className="nx-task-drawer__section">
          <h3>Timeline</h3>
          {log.isLoading ? (
            <SkeletonLoader shape="text-line" count={3} />
          ) : log.isError ? (
            <p className="nx-task-drawer__error">
              Failed to load log: {String((log.error as Error)?.message)}
            </p>
          ) : (
            <TaskTimeline entries={log.data ?? []} />
          )}
        </section>
      </div>
    </aside>
  );
}
